import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { InputField } from "../formElements/InputField";
import accountsFlowApi from "../../services/accountsFlowService";

/*
 * GSTIN, credit limit, credit days, opening balance and active for the Customer Master
 * register / edit forms (same fields as the Accounts module's customer dialog).
 * Stored on userMaster in the fields the order credit check and the Accounts sync read:
 *   gstNumber, creditLimit (+ creditLimitAmt), creditDays (+ creditPeriod), activeInactive, openingBalance.
 * The opening balance becomes the opening of the customer's Accounts ledger (backend
 * accounts_flow/customerAccount.applyCustomerOpening); it is locked once entries are posted.
 */

const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";
const isAmount = (value) => /^\d+(\.\d{1,2})?$/.test(String(value).trim());

/* Field errors ({} when fine). Credit limit and days are required (0 allowed). */
export function validateCreditFields(values = {}) {
  const errors = {};
  if (
    !isBlank(values.gstNumber) &&
    !GSTIN.test(String(values.gstNumber).trim().toUpperCase())
  )
    errors.gstNumber = "Enter a valid 15-character GSTIN, e.g. 29ABCDE1234F1Z5";
  if (isBlank(values.creditLimit))
    errors.creditLimit = "Credit limit is required (0 = no credit)";
  else if (!isAmount(values.creditLimit))
    errors.creditLimit = "Enter an amount of 0 or more";
  if (isBlank(values.creditDays))
    errors.creditDays = "Credit days are required";
  else if (
    !/^\d+$/.test(String(values.creditDays).trim()) ||
    Number(values.creditDays) > 365
  )
    errors.creditDays = "Enter whole days from 0 to 365";
  if (!isBlank(values.openingBalance) && !isAmount(values.openingBalance))
    errors.openingBalance = "Enter an amount of 0 or more";
  return errors;
}

/* The values to save on userMaster (numbers, mirrored fields). */
export function creditFieldsForSave(values = {}) {
  const limit = isBlank(values.creditLimit) ? 0 : Number(values.creditLimit);
  const days = isBlank(values.creditDays) ? 0 : Number(values.creditDays);
  const out = {
    gstNumber: String(values.gstNumber || "")
      .trim()
      .toUpperCase(),
    creditLimit: limit,
    creditLimitAmt: limit, // the order credit check reads creditLimitAmt first (Credit Policy); keep them equal
    creditDays: days,
    creditPeriod: days,
    activeInactive: values.activeInactive !== false,
  };
  if (!isBlank(values.openingBalance))
    out.openingBalance = Number(values.openingBalance);
  return out;
}

/* Opening balance as the Accounts ledger has it, and whether it can still be changed. */
export function useCustomerOpening(customerCode) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!customerCode) return;
    accountsFlowApi
      .customerOpening(customerCode)
      .then((r) => setState(r?.data || null))
      .catch(() => setState(null));
  }, [customerCode]);
  return state;
}

/* showActive: false where the form already has its own Active/Inactive switch (edit page). */
export default function CustomerCreditFields({
  values = {},
  onChange,
  errors = {},
  disabled = false,
  opening = null,
  showActive = true,
}) {
  const [open, setOpen] = useState(true);
  const set = (name) => (event) =>
    onChange(
      name,
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value,
    );
  const openingLocked = Boolean(opening && opening.locked);
  return (
    <div className="accordion mb-4">
      <div className="accordion-item">
        <h2 className="accordion-header">
          <Row className="accordion-button">
            <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
              <i
                className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`}
                onClick={() => setOpen(!open)}
              />
            </Col>
            <Col xs={1} md={1} lg={1} className="text-center logoBtn">
              <span className="iconLogo">CA</span>
            </Col>
            <Col xs={8} md={9} lg={9}>
              <span className="headerTitle">Credit &amp; Accounts</span>
              <span className="subHeaderTitle">
                GSTIN, credit limit, credit days and opening balance
              </span>
            </Col>
          </Row>
        </h2>
        <div
          className={`accordion-collapse collapse show ${open ? "visible" : ""}`}
        >
          <div className="accordion-body">
            <Row className="pt-2 pb-2">
              <Col
                xs={12}
                md={6}
                lg={4}
                className="commTopButtonRightLeftPadding"
              >
                <InputField
                  className="inputBox"
                  label="GSTIN"
                  name="gstNumber"
                  id="gstNumber"
                  placeholder="GSTIN"
                  maxLength={15}
                  value={values.gstNumber || ""}
                  onChange={set("gstNumber")}
                  error={errors.gstNumber}
                  disabled={disabled}
                />
              </Col>
              <Col
                xs={12}
                md={6}
                lg={4}
                className="commTopButtonRightLeftPadding"
              >
                <InputField
                  className="inputBox"
                  label="Credit limit"
                  required
                  name="creditLimit"
                  id="creditLimit"
                  placeholder="0"
                  value={values.creditLimit ?? ""}
                  onChange={set("creditLimit")}
                  error={errors.creditLimit}
                  disabled={disabled}
                />
                {!errors.creditLimit && (
                  <small className="text-muted">
                    Most the customer may owe at any time. 0 = no credit.
                  </small>
                )}
              </Col>
              <Col
                xs={12}
                md={6}
                lg={4}
                className="commTopButtonRightLeftPadding"
              >
                <InputField
                  className="inputBox"
                  label="Credit days"
                  required
                  name="creditDays"
                  id="creditDays"
                  placeholder="0"
                  value={values.creditDays ?? ""}
                  onChange={set("creditDays")}
                  error={errors.creditDays}
                  disabled={disabled}
                />
              </Col>
              <Col
                xs={12}
                md={6}
                lg={4}
                className="commTopButtonRightLeftPadding"
              >
                <InputField
                  className="inputBox"
                  label="Opening balance owed"
                  name="openingBalance"
                  id="openingBalance"
                  placeholder="0"
                  value={values.openingBalance ?? ""}
                  onChange={set("openingBalance")}
                  error={errors.openingBalance}
                  disabled={disabled || openingLocked}
                />
                {!errors.openingBalance && (
                  <small
                    className={openingLocked ? "text-danger" : "text-muted"}
                  >
                    {openingLocked
                      ? opening.reason
                      : "What the customer already owes when you start using this app, e.g. the balance from Tally."}
                  </small>
                )}
              </Col>
              {showActive && (
                <Col
                  xs={12}
                  md={6}
                  lg={4}
                  className="commTopButtonRightLeftPadding d-flex align-items-center"
                >
                  <div className="form-check mt-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="activeInactive"
                      checked={values.activeInactive !== false}
                      onChange={set("activeInactive")}
                      disabled={disabled}
                    />
                    <label
                      className="form-check-label fw-medium"
                      htmlFor="activeInactive"
                    >
                      Active
                    </label>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
}
